import { Component, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { AlertService, AlertType } from '@shared/services/alert.service';
import { RegraTributariaService } from '@shared/services/apis/motor-fiscal/regra-tributaria.service';
import { RegraTributariaErrorCodeEnum } from '@shared/enums/motor-fiscal/regra-tributaria-error-code.enum';
import { ModeloDocumentoService } from '@shared/services/apis/dfe/modelo-documento.service';
import { RegraTributariaNaturezaOperacaoStruct } from '@shared/services/structs/motor-fiscal/regra-tributaria-natureza-operacao.struct';
import { RegraTributariaSelectDialogData } from './regra-tributaria-select-dialog-data';

export { RegraTributariaSelectDialogData } from './regra-tributaria-select-dialog-data';

import { EmptyStateComponent } from '@shared/components/empty-state/empty-state.component';
import { LoadingBoxDefaultListComponent } from '@shared/components/loading-box-default-list/loading-box-default-list.component';

@Component({
    selector: 'app-regra-tributaria-select-dialog',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        MatButtonModule,
        MatCheckboxModule,
        MatDialogModule,
        MatFormFieldModule,
        MatIconModule,
        MatInputModule,
        MatProgressSpinnerModule,
        EmptyStateComponent,
        LoadingBoxDefaultListComponent,
    ],
    templateUrl: './regra-tributaria-select-dialog.component.html',
    styleUrl: './regra-tributaria-select-dialog.component.css',
})
export class RegraTributariaSelectDialogComponent implements OnInit {
    public pesquisaControl = new FormControl('');
    public regras: RegraTributariaNaturezaOperacaoStruct[] = [];
    public selecionadas: RegraTributariaNaturezaOperacaoStruct[] = [];
    public paginaAtual = 1;
    public totalRegistros = 0;
    public isLoading = false;
    // Modelo de documento pertence ao db_dfe: as regras trazem apenas o codigo; mapeamos codigo -> nome.
    public nomeModeloPorCodigo: Record<string, string> = {};

    private readonly tamanhoPagina = 20;

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: RegraTributariaSelectDialogData,
        private dialogRef: MatDialogRef<RegraTributariaSelectDialogComponent, RegraTributariaNaturezaOperacaoStruct[]>,
        private regraTributariaService: RegraTributariaService,
        private modeloDocumentoService: ModeloDocumentoService,
        private alertService: AlertService,
    ) {
        this.selecionadas = [...(data.selecionadas ?? [])];
    }

    ngOnInit(): void {
        this.carregarModelos();
        this.carregar(true);
    }

    private carregarModelos(): void {
        this.modeloDocumentoService.listar().subscribe({
            next: res => {
                this.nomeModeloPorCodigo = {};
                for (const m of (res.modelos || [])) {
                    if (m.codigo) {
                        this.nomeModeloPorCodigo[m.codigo] = m.nome || m.descricao || m.codigo;
                    }
                }
            },
            error: () => { /* fallback: exibe o codigo */ },
        });
    }

    public carregar(reset: boolean = false): void {
        if (reset) {
            this.paginaAtual = 1;
            this.regras = [];
        }

        this.isLoading = true;
        const pesquisa = this.pesquisaControl.value?.trim() || null;

        // O lookup traz só regras ativas, as únicas que podem ser vinculadas a uma natureza de operação.
        this.regraTributariaService.getLookup(pesquisa, this.paginaAtual, this.tamanhoPagina).subscribe({
            next: response => {
                if (response.isError) {
                    // Empresa sem regras: cria as regras padrão e recarrega o lookup (mesmo molde das listas).
                    if (response.errorCode === RegraTributariaErrorCodeEnum.SeedPendente) {
                        this.inicializarRegras(reset);
                        return;
                    }
                    this.isLoading = false;
                    this.alertService.show('Erro', response.errorDescription || 'Erro ao carregar regras tributárias', AlertType.error);
                    return;
                }
                this.isLoading = false;
                this.totalRegistros = response.totalRegistros;

                let novasRegras = response.regras;
                if (this.data.regraPadraoId) {
                    novasRegras = novasRegras.filter(item => item.idRegraTributaria !== this.data.regraPadraoId);
                    if (response.regras.some(item => item.idRegraTributaria === this.data.regraPadraoId)) {
                        this.totalRegistros = Math.max(0, this.totalRegistros - 1);
                    }
                }
                
                this.regras = reset ? novasRegras : [...this.regras, ...novasRegras];
            },
            error: () => {
                this.isLoading = false;
            },
        });
    }

    /** Cria as regras tributárias padrão da empresa e recarrega o lookup. */
    private inicializarRegras(reset: boolean): void {
        this.alertService.show('Informação', 'Carregando as configurações base...', AlertType.info);
        this.regraTributariaService.seed().subscribe({
            next: seed => {
                if (seed.isError) {
                    this.isLoading = false;
                    this.alertService.show('Erro', seed.errorDescription || 'Erro ao inicializar as regras tributárias', AlertType.error);
                    return;
                }
                this.carregar(reset);
            },
            error: err => {
                this.isLoading = false;
                this.alertService.show('Erro inesperado', err.message || 'Erro ao inicializar as regras tributárias', AlertType.error);
            },
        });
    }

    public pesquisar(): void {
        this.carregar(true);
    }

    public verMais(): void {
        this.paginaAtual++;
        this.carregar(false);
    }

    public isSelecionada(regra: RegraTributariaNaturezaOperacaoStruct): boolean {
        return this.selecionadas.some(item => item.idRegraTributaria === regra.idRegraTributaria);
    }

    public toggle(regra: RegraTributariaNaturezaOperacaoStruct): void {
        if (!this.data.multiple) {
            this.selecionadas = [regra];
            return;
        }

        if (this.isSelecionada(regra)) {
            this.selecionadas = this.selecionadas.filter(item => item.idRegraTributaria !== regra.idRegraTributaria);
            return;
        }

        this.selecionadas = [...this.selecionadas, regra];
    }

    public confirmar(): void {
        this.dialogRef.close(this.selecionadas);
    }

    public cancelar(): void {
        this.dialogRef.close(undefined);
    }

    public abrirCadastro(): void {
        window.open('/configuracoes/reforma-tributaria/regras/cadastrar', '_blank');
    }
}
